import React from "react";
import { HERO, HERO_IMAGE, LOGO_URL } from "@/content/veguk";
import { HERO as HERO_IDS, PDF } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { ArrowDown, Leaf, Download } from "lucide-react";

const PDF_URL = `${process.env.REACT_APP_BACKEND_URL}/api/pdf/definitions`;

export const Hero = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="hero"
      data-testid={HERO_IDS.root}
      className="relative min-h-[100vh] flex items-center pt-28 pb-20 overflow-hidden"
    >
      {/* Background image with overlay */}
      <div className="absolute inset-0 -z-10">
        <img
          src={HERO_IMAGE}
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#F9F6F0]/88" />
        <div className="absolute inset-0 veg-grain opacity-60" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center w-full">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3 mb-6">
            <Leaf size={16} className="text-[#2E5C46]" />
            <span className="veg-overline">
              A voluntary UK project
            </span>
          </div>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl text-[#2C352D] leading-[1.02] tracking-tight">
            Defining what it means
            <br />
            to be <em className="text-[#2E5C46] not-italic">vegetarian</em> in the UK.
          </h1>
          <p className="mt-8 text-lg text-[#4A5D4E] max-w-2xl leading-relaxed">
            VegUK clearly defines the recognised vegetarian types, their exceptions, and the
            requirements for the preparation and serving of vegetarian food — for caterers,
            care homes, schools, hospitals, airlines and councils across the United Kingdom.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <Button
              data-testid={HERO_IDS.ctaTypes}
              onClick={() => scrollTo("types")}
              className="bg-[#2E5C46] hover:bg-[#234836] text-[#F9F6F0] rounded-md px-7 py-6 text-sm tracking-wide"
            >
              Explore the five types
            </Button>
            <Button
              data-testid={HERO_IDS.ctaContact}
              onClick={() => scrollTo("contact")}
              variant="outline"
              className="border-[#2E5C46] text-[#2E5C46] hover:bg-[#2E5C46] hover:text-[#F9F6F0] rounded-md px-7 py-6 text-sm tracking-wide bg-transparent"
            >
              Contact the project
            </Button>
            <a
              data-testid={PDF.downloadHero}
              href={PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-md text-sm tracking-wide font-medium text-[#2C352D] hover:text-[#2E5C46] underline underline-offset-4 decoration-[#D1C9B9] hover:decoration-[#2E5C46] transition-colors"
            >
              <Download size={16} /> Download print-ready PDF
            </a>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="relative max-w-md mx-auto">
            <div className="absolute -inset-6 bg-[#EAE3D9] rounded-full -z-10 blur-2xl opacity-60" />
            <div className="bg-white border border-[#D1C9B9] rounded-full p-10 shadow-[0_30px_60px_-30px_rgba(44,53,45,0.25)]">
              <img
                src={LOGO_URL}
                alt="Vegetarian Food Definition — VegUK"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={() => scrollTo("about")}
        aria-label="Scroll to About"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#2E5C46] animate-bounce"
      >
        <ArrowDown size={22} />
      </button>
    </section>
  );
};
